import { useState, type ReactNode } from "react";

/**
 * In-app confirmation dialog — the amendie-styled replacement for the browser's `confirm()`.
 * Reuses the `.modal-backdrop`/`.modal` idiom (click-outside + Cancel dismiss). Render it
 * conditionally while a choice is pending; `onConfirm`/`onCancel` resolve it. Pass `busy` to
 * show progress and lock the buttons while the confirmed action runs.
 *
 * `confirmPhrase` turns it into a type-to-confirm dialog: the operator must type that exact string
 * (e.g. the client name) before the confirm button unlocks — the safety rail for irreversible ops.
 */
export function ConfirmModal({
  title,
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false,
  busy = false,
  confirmPhrase,
  onConfirm,
  onCancel,
}: {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  busy?: boolean;
  confirmPhrase?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState("");
  const locked = confirmPhrase != null && typed.trim() !== confirmPhrase;

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onCancel}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(440px, 100%)" }}
      >
        <div className="section-head">
          <h2>{title}</h2>
        </div>
        {body && (
          <p className="note" style={{ marginTop: "var(--sp4)" }}>
            {body}
          </p>
        )}
        {confirmPhrase != null && (
          <input
            autoFocus
            value={typed}
            disabled={busy}
            placeholder={confirmPhrase}
            onChange={(e) => setTyped(e.target.value)}
            style={{ marginTop: "var(--sp4)", width: "100%" }}
          />
        )}
        <div className="row-gap" style={{ marginTop: "var(--sp5)" }}>
          <div style={{ flex: 1 }} />
          <button className="btn ghost" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            className={danger ? "btn danger" : "btn"}
            onClick={onConfirm}
            disabled={busy || locked}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
